import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  ConfigurationState,
  LaunchDarklyService,
  PurchasedItemDto,
  ReportType,
  TitleAddress,
  DateUtilsService,
  SellerRemitDto,
  Configuration
} from 'remarketing-angular-library';
import { get } from 'lodash';
import { FeeToolTipService } from '../../../../../shared/services/feetooltip/feetooltip.service';
import { Store } from '@ngxs/store';
import {
  formatPhoneNumber,
  CurrencyFormatService,
  SaComponent,
  FormatPhoneNumberPipe
} from 'smartauction-frontend-micro-library';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { skipWhile, takeUntil } from 'rxjs/operators';
import { checkMobile } from '../../../../shared/utils/util';
import { FeeTooltipFormatterService } from '../../../../../../src/app/shared/fee-tooltip-formatter/fee-tooltip-formatter.service';
import { FeesKeyMap } from 'remarketing-angular-library/interfaces/fee-tooltip';
import { ReportDamagesTableComponent } from '../report-damages-table/offer-list-vehicle-table/report-damages-table.component';
import { RouterLink } from '@angular/router';
import { DatalistItemComponent, ConditionIndicatorComponent, VehicleDisclosuresIndicatorComponent } from 'remarketing-ui-components-lib';
import { NgTemplateOutlet, UpperCasePipe, DecimalPipe, KeyValuePipe } from '@angular/common';

@Component({
    selector: 'app-report-content',
    templateUrl: './report-content.component.html',
    styleUrls: ['./report-content.component.scss'],
  imports: [
    TranslocoDirective,
    DatalistItemComponent,
    ConditionIndicatorComponent,
    NgTemplateOutlet,
    RouterLink,
    ReportDamagesTableComponent,
    UpperCasePipe,
    DecimalPipe,
    KeyValuePipe,
    FormatPhoneNumberPipe,
    VehicleDisclosuresIndicatorComponent
  ]
})
export class ReportContentComponent extends SaComponent implements OnInit, AfterViewInit {
  public static readonly AUTHORIZATION = 'AUTHORIZATION';
  isAuthReport = true;

  @Input() isFirst: boolean;
  @Input() isLast: boolean;
  @Input() selectedReportType: ReportType;
  @Input() previewType: ReportType;
  @Input() purchasedItem: PurchasedItemDto;
  @Input() sellerRemitDto: SellerRemitDto;
  @Input() reportList: ReportType[] = [];
  @ViewChild('note') note: ElementRef;
  @ViewChild('content') content: ElementRef;

  configuration: Configuration;
  translatedObj: any;
  conditionIndicatorTranslations: any;

  altTranslations = {
    distanceUnits: '',
    disclaimer: '',
    termsOfUseLabel: ''
  }

  constructor(
    private readonly tooltipService: FeeToolTipService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly _store: Store,
    private readonly translocoService: TranslocoService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly feeTooltipFormatterService: FeeTooltipFormatterService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {
    super();
  }

  private _buyFees: FeesKeyMap;
  private _sellFees: FeesKeyMap;

  get previewTitle(): string {
    switch (this.previewType) {
      case ReportType.AUTHORIZATION:
        return this.translatedObj.textAuthorizationReport;
      case ReportType.RECORD_OF_SALE:
        return this.translatedObj.textRecordofSale;
      case ReportType.SELLER_INVOICE:
        return this.translatedObj.textSellerInvoice;
    }
  }

  get isDisclosureLinkEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-disclosuresEnabled', true);
  }

  get isBuyerDetailEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-buyerDetailEnabled', true);
  }

  get isAuthorizationEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-authorizationEnabled', true);
  }

  get isOdometerDisclosureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-odometerDisclosureEnabled', false);
  }

  get isStockNumberEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('stockUnitEnabled', true);
  }

  get isSellerPermitAndLicenseNumberEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-sellerPermitAndLicenseNumberEnabled', false);
  }

  get isTenantCanadian(): boolean {
    return this._launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isContactSupportEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('auctionSummary-ros-contactSupportEnabled', true);
  }
  
  get isSellSellerInvoiceReportVerbiageUpdateEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-sellerInvoiceReport-verbiageUpdateEnabled', false);
  }
  
  get isSellerRemittedBuyerTaxesAfterSaleEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-sellerInvoiceReport-sellerRemittedBuyerTaxesAfterSaleEnabled', false);
  }

  get hasConditionIndicators(): boolean {
    return (
      get(this.purchasedItem, 'disclosures', false) &&
      (get(this.purchasedItem, 'disclosures.limitedArbitration', false) ||
        get(this.purchasedItem, 'disclosures.yellowLights', []).length > 0)
    );
  }

  get sellerLicenseNumber(): string {
    return this.purchasedItem.seller.licenseNumber ? this.purchasedItem.seller.licenseNumber : 'N/A';
  }

  get sellerFederalNumber(): string {
    return this.purchasedItem.seller.federalTaxId ? this.purchasedItem.seller.federalTaxId : 'N/A';
  }

  get buyerLicenseNumber(): string {
    return this.purchasedItem.buyer.licenseNumber ? this.purchasedItem.buyer.licenseNumber : 'N/A';
  }

  get buyerFederalNumber(): string {
    return this.purchasedItem.buyer.federalTaxId ? this.purchasedItem.buyer.federalTaxId : 'N/A';
  }

  get sellerPermitNumber(): string {
    return this.purchasedItem.seller.sellerPermitNumber ? this.purchasedItem.seller.sellerPermitNumber : 'N/A';
  }

  get buyerPermitNumber(): string {
    return this.purchasedItem.buyer.sellerPermitNumber ? this.purchasedItem.buyer.sellerPermitNumber : 'N/A';
  }

  get isNotLastOrActive() {
    return !this.isFirst && this.selectedReportType !== ReportType.BOTH && this.reportList.length < 2;
  }

  get isSellerInvoice() {
    return this.previewType === ReportType.SELLER_INVOICE;
  }

  get hasRedLight(): boolean {
    return get(this.purchasedItem, 'disclosures.limitedArbitration', false);
  }

  get hasYellowLight(): boolean {
    return get(this.purchasedItem, 'disclosures.yellowLights', []).length > 0;
  }

  get yellowLightsHasUnknownKilometers(): boolean {
    return get(this.purchasedItem, 'disclosures.yellowLights', []).includes('True Kilometers Unknown');
  }

  get hasSellerDisclosureComment(): boolean {
    return get(this.purchasedItem, 'conditionReport.sellerDisclosureComment', null) !== null;
  }

  get hasOdometerDisclosure(): boolean {
    return (
      !this.isAuthReport &&
      this.isOdometerDisclosureEnabled &&
      get(this.purchasedItem, 'disclosures.yellowLights.length', 0) > 0
    );
  }

  get hasDamages(): boolean {
    return get(this.purchasedItem, 'conditionReport.damages.length', 0) > 0;
  }

  get hasDisclosures(): boolean {
    return this.hasSellerDisclosureComment || this.hasOdometerDisclosure || this.hasDamages;
  }

  get vehicleDisplayName() {
    return (
      `${get(this.purchasedItem, 'itemDetail.year', '')} ` +
      `${get(this.purchasedItem, 'itemDetail.make', '')} ` +
      `${get(this.purchasedItem, 'itemDetail.model', '')} ` +
      `${get(this.purchasedItem, 'itemDetail.trim', '')} `
    ).trim();
  }

  get vehicleDrivetrainEngineSize() {
    return (
      `${get(this.purchasedItem, 'itemDetail.driveTrainAbbrev', '')} ` +
      `${get(this.purchasedItem, 'itemDetail.engineSize', '')} `
    );
  }

  get buyerName() {
    return `${get(this.purchasedItem, 'buyer.name', '')}`;
  }

  get buyerAddressOne() {
    return `${get(this.purchasedItem, 'buyer.address1', '')}`;
  }

  get buyerCityState() {
    return `${get(this.purchasedItem, 'buyer.city')}, ${get(this.purchasedItem, 'buyer.state')}`;
  }

  get buyerZipCode() {
    return `${get(this.purchasedItem, 'buyer.zip', '')}`;
  }

  get buyerPhone() {
    const buyerPhoneNumber = get(this.purchasedItem, 'buyer.contactPhone', '');
    if (!buyerPhoneNumber) {
      return 'N/A';
    }
    const formatPhone = formatPhoneNumber(buyerPhoneNumber);
    return formatPhone !== null ? formatPhone : buyerPhoneNumber;
  }

  get odometerDisclosure() {
    if (this.hasYellowLight) {
      if (this.yellowLightsHasUnknownKilometers) {
        return this.translatedObj.textOdometerDisclosuresYellowLight;
      } else {
        const odomText = this.translatedObj.textOdometerDisclosures;
        return odomText
          .replace('<PostingDate>', this.dateUtilsService.localizeDate(this.purchasedItem.itemDetail.postedDate))
          .replace('<OdometerValue>', this.purchasedItem.itemDetail.odometer.toString());
      }
    }
  }

  get isPreviewTitle() {
    return this.isFirst || this.selectedReportType === 'BOTH' || this.reportList.length > 1;
  }

  get titleAddressValue(): any | {} {
    return get(this.purchasedItem, 'titleAddress', {});
  }

  get titleAddressName() {
    return this.titleAddress('name') ? this.titleAddress('name') : 'N/A';
  }

  get titleAddressDetail() {
    return this.titleAddress('addressLine1') ? this.titleAddress('addressLine1') : 'N/A';
  }

  get titleAddressDetail2() {
    return this.titleAddress('addressLine2') ? this.titleAddress('addressLine2') : '';
  }

  get titleAddressRegion() {
    const city = this.titleAddress('city') ? this.titleAddress('city') : 'N/A';
    const state = this.titleAddress('state') ? this.titleAddress('state') : 'N/A';
    const zip = this.titleAddress('zip') ? this.titleAddress('zip') : 'N/A';
    return `${city}, ${state} ${zip}`;
  }

  get totalCost() {
    const salePrice = get(this.purchasedItem, 'salePrice', 0);
    if (this.selectedReportType === ReportType.SELLER_INVOICE) {
      const taxSource = this.isSellerRemittedBuyerTaxesAfterSaleEnabled
        ? this.purchasedItem 
        : this.sellerRemitDto;
      return salePrice +
        get(taxSource, 'proceedsFederalTax', 0) +
        get(taxSource, 'proceedsStateTax', 0) - get(this.purchasedItem, 'sellFees.total', 0);
    }
    return salePrice + get(this.purchasedItem, 'buyFees.total', 0);
  }

  get showSellerComment(): boolean {
    return get(this.purchasedItem, 'sellerPaidComment', '').length > 0;
  }

  get showTitleMessage(): boolean {
    return get(this.purchasedItem, 'financeSource.titleMessage', '').length > 0;
  }

  get showSellerDisclosure(): boolean {
    return get(this.purchasedItem, 'seller.comment', '').length > 0;
  }

  get sellerAddressOne() {
    return `${get(this.purchasedItem, 'seller.address1', '')}`;
  }

  get sellerCityState() {
    return `${get(this.purchasedItem, 'seller.city')}, ${get(this.purchasedItem, 'seller.state')}`;
  }

  get sellerZipCode() {
    return `${get(this.purchasedItem, 'seller.zip', '')}`;
  }

  get tenantName() {
    return get(this.configuration, 'tenantFullName', '');
  }

  get platformAddress() {
    return get(this.configuration, 'platform.address', '');
  }

  get platformProvider() {
    const name = get(this.configuration, 'platform.name', '');
    const quebecReg = get(this.configuration, 'platform.quebecRegistrationNumber', '');
    const federalTaxId = get(this.configuration, 'platform.federalTaxId', '');
    return `${name} (Business #: ${federalTaxId}; Quebec Registration #: ${quebecReg})`;
  }

  get showStoringLocation(): boolean {
    return (
      get(this.purchasedItem, 'storageLocation.pickupComment', '').length > 0 ||
      get(this.purchasedItem, 'storageLocation.additionalPickupComment', '').length > 0
    );
  }

  get showStorageContact() {
    if (this.purchasedItem.storageLocation.contactName) {
      return this.purchasedItem.storageLocation.contactName;
    } else {
      return 'N/A';
    }
  }

  get showStoragePhone() {
    if (!this.purchasedItem.storageLocation.contactPhone) {
      return 'N/A';
    }
    const formatPhone = formatPhoneNumber(this.purchasedItem.storageLocation.contactPhone);
    return formatPhone !== null ? formatPhone : this.purchasedItem.storageLocation.contactPhone;
  }

  get transportationCost() {
    if (this.purchasedItem.transportationOrder) return get(this.purchasedItem, 'transportationQuote.quoteAmount', 0);
  }

  get transportationName() {
    if (this.purchasedItem.transportationOrder) return get(this.purchasedItem, 'transportationOrder.providerName');
  }

  get transportationEmail() {
    if (this.purchasedItem.transportationOrder) return get(this.purchasedItem, 'transportationOrder.providerEmail');
  }

  get transportationNumber() {
    if (this.purchasedItem.transportationOrder)
      return formatPhoneNumber(this.purchasedItem.transportationOrder.providerPhone);
  }

  get isShowBuyerDetails() {
    return (
      this.isBuyerDetailEnabled ||
      (!this.isBuyerDetailEnabled &&
        (this.selectedReportType === ReportType.BOTH || this.reportList.length > 1) &&
        this.isAuthReport) ||
      this.isLast
    );
  }

  get isRecordOfSaleMobile() {
    return checkMobile() && !this.isAuthReport;
  }

  get isAuthReportMobile() {
    return checkMobile() && this.isAuthReport;
  }

  get isMobile() {
    return checkMobile();
  }

  get taxSource() {
    return this.isSellerRemittedBuyerTaxesAfterSaleEnabled 
      ? this.purchasedItem 
      : this.sellerRemitDto;
  }

  ngOnInit() {
    this.configuration = this._store.selectSnapshot(ConfigurationState.configuration);
    this.getTranslations();
    this.setPreviewType();
    this.combinedBuyFeesMap();
    this.combinedSellFeesMap();
  }

  /**
   * AuctionItem Title Address Detail
   */
  titleAddress(key: keyof TitleAddress): string {
    return get(this.titleAddressValue, key, '');
  }

  abs(number) {
    return Math.abs(number);
  }

  // @TODO: Abstract to NGXS state and create selector to handle disclosureInventoryItemUuid
  setInventoryItemUuidIntoSession(event) {
    sessionStorage.setItem('disclosureInventoryItemUuid', event.target.id);
  }

  combinedBuyFeesMap() {
    this._buyFees = this.feeTooltipFormatterService.getFeeMap(
      this.tooltipService.translatedFees(this.purchasedItem.buyFees.fees)
    );
  }

  combinedSellFeesMap() {
    this._sellFees = this.feeTooltipFormatterService.getFeeMapWithCombinedFederalAndStateTaxes(
      this.tooltipService.translatedFees(this.purchasedItem.sellFees.fees)
    );
  }

  formatCurrency(currency, withCents?) {
    return this.currencyFormatService.formatCurrency(currency, withCents);
  }

  localizeDate(date) {
    return this.dateUtilsService.localizeDate(date);
  }

  setPreviewType() {
    this.isAuthReport = this.previewType === ReportType.AUTHORIZATION;
  }

  // This function is necessary to preserve the order of buy fees as they come back from the API
  disableKeyValueSort() {
    return 0;
  }

  updateDisclaimerTranslations() {
    const noteContent = this.translatedObj?.note;
    const contentValue = this.translatedObj?.termsOfUse.content;

    if (this.note?.nativeElement && noteContent) {
      this.note.nativeElement.innerHTML = noteContent;
    }

    if (this.content?.nativeElement && contentValue) {
      this.content.nativeElement.innerHTML = contentValue;
    }
  }

  ngAfterViewInit(): void {
    this.updateDisclaimerTranslations();
  }

  getTranslations() {
    const keyParams = {
      'reports.reportContent': {},
      'UIComponents.conditionIndicatorTranslations': {},
      'common.distanceUnit': {},
      'pcs.tenant.termsOfUseShort': {}
    };

    this.translocoService.selectTranslateObject(keyParams)
    .pipe(
      takeUntil(this.destroyed$)
    )
    .subscribe(value => {
      this.translatedObj = value[0];
      this.conditionIndicatorTranslations = value[1];
      this.altTranslations.distanceUnits = value[2];
      this.altTranslations.termsOfUseLabel = value[3];
    });

    // Get Disclaimer after previously translating the termsOfUseLabel
    this.translocoService.selectTranslate(
      'reports.reportContent.disclaimer', {purchasedItemBuyerName: this.purchasedItem.buyer.name, termsOfUseShort: this.altTranslations.termsOfUseLabel}
    )
    .pipe(
      skipWhile(ready => !ready),
      takeUntil(this.destroyed$)
    )
    .subscribe(disclaimer => {
      this.altTranslations.disclaimer = disclaimer;
    });
  }
}
